/**
 * BatchReviewLayout - Layout générique pour la review batch (dépenses ET factures)
 *
 * Composant partagé : header avec stats, grille de cartes, actions par carte.
 * Le contenu spécifique de chaque carte est fourni via renderCardContent (render prop).
 *
 * Design System: Dashboard context (text-gray-900, bg-white/70, teal-600)
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  X,
  Edit,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui-v2';
import type { ReviewItem, BatchReviewStats } from '../hooks/useBatchReview';

interface BatchReviewLayoutProps<T> {
  stats: BatchReviewStats;
  items: ReviewItem<T>[];
  isCreating: boolean;
  onBack: () => void;
  onValidateAllPending: () => void;
  onCreateAll: () => void;
  createLabel: string;
  onValidate: (index: number) => void;
  onReject: (index: number) => void;
  onEdit: (index: number) => void;
  onConfirmEdit: (index: number) => void;
  onCancelEdit: (index: number) => void;
  renderCardContent: (item: ReviewItem<T>, index: number, isEditing: boolean) => ReactNode;
}

export function BatchReviewLayout<T>({
  stats,
  items,
  isCreating,
  onBack,
  onValidateAllPending,
  onCreateAll,
  createLabel,
  onValidate,
  onReject,
  onEdit,
  onConfirmEdit,
  onCancelEdit,
  renderCardContent,
}: BatchReviewLayoutProps<T>) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-light tracking-tight text-gray-900">
            {t('expenses.batch.detected', '{{count}} facture(s) détectée(s)', { count: stats.total })}
          </h2>
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="w-4 h-4" /> {stats.validated} {t('expenses.batch.validated', 'validée(s)')}
            </span>
            <span className="flex items-center gap-1 text-amber-600">
              <AlertTriangle className="w-4 h-4" /> {stats.pending} {t('expenses.batch.pending', 'en attente')}
            </span>
            <span className="flex items-center gap-1 text-red-500">
              <XCircle className="w-4 h-4" /> {stats.rejected} {t('expenses.batch.rejected', 'rejetée(s)')}
            </span>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            {t('common.actions.back', 'Retour')}
          </Button>
          {stats.pending > 0 && (
            <Button variant="outline" onClick={onValidateAllPending}>
              {t('expenses.batch.validateAll', 'Tout valider ({{count}})', { count: stats.pending })}
            </Button>
          )}
          {stats.validated > 0 && (
            <Button
              variant="secondary"
              onClick={onCreateAll}
              disabled={isCreating}
              icon={isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            >
              {isCreating ? t('expenses.batch.creating', 'Création...') : createLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((item, index) => {
          const confidence = item.ocrResult.confidenceScore;
          const isEditing = item.status === 'editing';

          return (
            <div
              key={index}
              className={`
                bg-white/20 backdrop-blur-xl rounded-3xl border shadow-xl transition-all duration-300
                ${item.status === 'validated' ? 'border-green-300/50 bg-green-50/10' : ''}
                ${item.status === 'rejected' ? 'border-red-300/50 bg-red-50/10 opacity-60' : ''}
                ${item.status === 'pending' ? 'border-white/30' : ''}
                ${isEditing ? 'border-teal-400/50 ring-2 ring-teal-500/20' : ''}
              `}
            >
              {/* Card header */}
              <div className="flex items-center justify-between p-4 border-b border-white/20">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate block">{item.ocrResult.filename}</span>
                    {item.ocrResult.data?.page_start != null && (
                      <span className="text-xs text-gray-500">
                        {item.ocrResult.data.page_start === item.ocrResult.data.page_end
                          ? `Page ${item.ocrResult.data.page_start}`
                          : `Pages ${item.ocrResult.data.page_start}-${item.ocrResult.data.page_end}`
                        }
                      </span>
                    )}
                  </div>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-xs font-semibold
                  ${confidence >= 90 ? 'bg-green-100 text-green-700' : ''}
                  ${confidence >= 70 && confidence < 90 ? 'bg-amber-100 text-amber-700' : ''}
                  ${confidence < 70 ? 'bg-red-100 text-red-700' : ''}
                `}>
                  {confidence}%
                </div>
              </div>

              {/* Card content — spécifique via render prop */}
              <div className="p-4 space-y-3">
                {!item.ocrResult.success ? (
                  <div className="text-sm text-red-600 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    {item.ocrResult.error || t('expenses.batch.extractionFailed', 'Extraction échouée')}
                  </div>
                ) : (
                  renderCardContent(item, index, isEditing)
                )}
              </div>

              {/* Card actions */}
              {item.ocrResult.success && item.status !== 'rejected' && (
                <div className="flex items-center gap-2 p-4 border-t border-white/20">
                  {isEditing ? (
                    <>
                      <button onClick={() => onConfirmEdit(index)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-xl transition-colors">
                        <Check className="w-4 h-4" /> {t('common.actions.confirm', 'Confirmer')}
                      </button>
                      <button onClick={() => onCancelEdit(index)} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-xl transition-colors">
                        {t('common.actions.cancel', 'Annuler')}
                      </button>
                    </>
                  ) : (
                    <>
                      {item.status !== 'validated' && (
                        <button onClick={() => onValidate(index)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-xl transition-colors">
                          <Check className="w-4 h-4" /> {t('common.actions.validate', 'Valider')}
                        </button>
                      )}
                      <button onClick={() => onEdit(index)} className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors">
                        <Edit className="w-4 h-4" /> {t('common.actions.correct', 'Corriger')}
                      </button>
                      <button onClick={() => onReject(index)} className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
